import { useCallback, useEffect, useState } from 'react'
import { Activity, ArrowLeft, Blocks, Building2, ClipboardList, Database, Flag, FolderOpen, Gauge, LayoutDashboard, LockKeyhole, Menu, PackageOpen, RefreshCw, Settings2, ShieldCheck, SlidersHorizontal, UserCog, Users, X } from 'lucide-react'
import { Link } from 'react-router-dom'
import { AccountsView, CasesView, DocumentsView, LogsView, OverviewView, PermissionsView, StaffView } from '../components/admin/AdminOperationsViews'
import { AdsView, CommerceView, DriveStorageView, EditorView, FeatureView, NavigationView, SettingsView } from '../components/admin/AdminExperienceViews'
import type { AdminViewKey, PortalKind } from '../components/admin/types'
import { Brand } from '../components/Brand'
import { DisplayControls } from '../components/DisplayControls'
import { useAuth } from '../core/auth'
import { loadAdminConfig, saveAdminSection, type AdminConfig } from '../data/platformRepository'

const menuGroups = {
  super: [
    { label: '운영', items: [['overview', '운영 현황', LayoutDashboard], ['cases', '진료기록', ClipboardList], ['accounts', '계정 관리', UserCog], ['staff', '담당자 관리', Users], ['documents', '문서·파일', FolderOpen]] },
    { label: '권한·보안', items: [['permissions', '권한 설정', ShieldCheck], ['logs', '접속·감사 로그', Activity]] },
    { label: '화면·기능', items: [['features', '기능 설정', SlidersHorizontal], ['editor', '페이지 편집', Blocks], ['navigation', '하단 메뉴', Menu], ['ads', '광고 위치', Flag]] },
    { label: '판매', items: [['commerce', '상품·결제', PackageOpen]] },
    { label: '시스템', items: [['storage', '파일 저장소', Database], ['settings', '서비스 설정', Settings2]] },
  ],
  hospital: [
    { label: '진료 업무', items: [['overview', '업무 현황', LayoutDashboard], ['cases', '공유 기록 열기', ClipboardList], ['staff', '병원 담당자', Users], ['documents', '병원 문서·파일', FolderOpen]] },
    { label: '권한·보안', items: [['permissions', '담당자 권한', ShieldCheck], ['logs', '접속·감사 로그', Activity]] },
    { label: '병원 설정', items: [['settings', '병원 정보', Settings2]] },
  ],
} as const

function PortalHeader({ kind, onMenu }: { kind: PortalKind; onMenu: () => void }) {
  const { user, logout } = useAuth()
  return <header className="portal-header"><button className="portal-menu-trigger" onClick={onMenu} aria-label="관리 메뉴 열기"><Menu /></button><div className="portal-header-title"><span>{kind === 'super' ? <ShieldCheck /> : <Building2 />}</span><div><strong>{kind === 'super' ? '최고 관리자' : '병원 관리자'}</strong><small>{kind === 'super' ? '서비스 전체 운영' : user?.organization || '병원 전용'}</small></div></div><div className="portal-header-actions"><DisplayControls /><div className="account-menu"><span>{user?.name?.slice(0, 1) || 'A'}</span><div><strong>{user?.name}</strong><small>{user?.loginId}</small></div></div><button className="text-button" onClick={() => void logout()}>로그아웃</button></div></header>
}

export function PortalPage({ kind }: { kind: PortalKind }) {
  const [view, setView] = useState<AdminViewKey>(() => kind === 'super' && new URLSearchParams(window.location.search).has('drive') ? 'storage' : 'overview')
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [data, setData] = useState<AdminConfig | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const { csrf } = useAuth()
  const groups = menuGroups[kind]

  const reload = useCallback(async () => {
    setLoading(true); setError('')
    try { setData(await loadAdminConfig()) }
    catch (cause) { setError(cause instanceof Error ? cause.message : '운영 정보를 불러오지 못했습니다.') }
    finally { setLoading(false) }
  }, [])

  useEffect(() => {
    let active = true
    const controller = new AbortController()
    loadAdminConfig(controller.signal)
      .then((next) => { if (active) setData(next) })
      .catch((cause) => { if (active && cause instanceof Error && cause.name !== 'AbortError') setError(cause.message) })
      .finally(() => { if (active) setLoading(false) })
    return () => { active = false; controller.abort() }
  }, [])

  const save = async (section: string, value: unknown, successMessage: string) => {
    setSaving(true); setError('')
    try { await saveAdminSection(section, value, csrf); setNotice(successMessage); window.setTimeout(() => setNotice(''), 3200); await reload() }
    catch (cause) { const message = cause instanceof Error ? cause.message : '저장하지 못했습니다.'; setError(message); throw cause }
    finally { setSaving(false) }
  }

  const common = data ? { kind, data, save, reload, saving } : null
  const render = () => {
    if (!common) return null
    switch (view) {
      case 'overview': return <OverviewView {...common} />
      case 'cases': return <CasesView {...common} />
      case 'accounts': return <AccountsView />
      case 'staff': return <StaffView {...common} />
      case 'documents': return <DocumentsView {...common} />
      case 'permissions': return <PermissionsView {...common} />
      case 'logs': return <LogsView {...common} />
      case 'features': return <FeatureView {...common} />
      case 'editor': return <EditorView {...common} />
      case 'navigation': return <NavigationView {...common} />
      case 'ads': return <AdsView {...common} />
      case 'commerce': return <CommerceView {...common} />
      case 'storage': return <DriveStorageView />
      case 'settings': return <SettingsView {...common} />
      default: return null
    }
  }

  return <div className="portal-shell commercial-portal"><aside className={`portal-sidebar ${sidebarOpen ? 'open' : ''}`}><div className="sidebar-brand"><Brand /><button onClick={() => setSidebarOpen(false)} aria-label="관리 메뉴 닫기"><X /></button></div><div className="portal-label"><span>{kind === 'super' ? <ShieldCheck /> : <Building2 />}</span><div><strong>{kind === 'super' ? '최고 관리자' : '병원 관리자'}</strong><small>{kind === 'super' ? '서비스 운영 전용' : '병원 업무 전용'}</small></div></div><nav aria-label="관리 메뉴">{groups.map((group) => <div className="menu-group" key={group.label}><span>{group.label}</span>{group.items.map(([key, label, Icon]) => { const C = Icon as typeof LayoutDashboard; return <button key={key} className={view === key ? 'active' : ''} onClick={() => { setView(key as AdminViewKey); setSidebarOpen(false) }}><C />{label}</button> })}</div>)}</nav><div className="sidebar-footer"><Link to="/"><ArrowLeft />공개 플랫폼으로</Link><div><span>AC</span><p><strong>Animal Care</strong><small>myanimal.care</small></p><Gauge /></div></div></aside>{sidebarOpen && <button className="sidebar-scrim" onClick={() => setSidebarOpen(false)} aria-label="관리 메뉴 닫기" />}<div className="portal-main"><PortalHeader kind={kind} onMenu={() => setSidebarOpen(true)} />{notice && <div className="portal-toast"><ShieldCheck />{notice}</div>}{error && <div className="portal-error" role="alert"><span>{error}</span><button onClick={() => setError('')}><X /></button></div>}<main>{loading ? <div className="portal-loading-page"><span className="loading-ring" /><h1>운영 정보를 불러오는 중입니다.</h1><p>권한 범위 안의 최신 데이터만 확인합니다.</p></div> : data ? render() : <div className="portal-loading-page"><LockKeyhole /><h1>운영 정보를 불러오지 못했습니다.</h1><p>잠시 후 다시 시도해 주세요.</p><button className="secondary-button" onClick={() => void reload()}><RefreshCw />다시 시도</button></div>}</main></div></div>
}
